<template>
  <modal
    :id="modalId"
    ref="modal"
    class="add-actor-to-article__modal with-subtitle"
    @close="close"
    :title="$t('add_organisation_manually')"
    is-simplified-modal
  >
    <template v-slot:title>
      <h2 v-if="!showSuccessState" class="title">You are inviting {{ invitationMode === 'bulk' ? 'new community members' : 'a new community member' }}</h2>
      <h2 v-else class="title">
        <span v-if="invitationMode === 'bulk' && bulkInvitationResults.successful.length === 0">
          Invitation Failed
        </span>
        <span v-else-if="invitationMode === 'bulk' && bulkInvitationResults.failed.length > 0">
          Invitation Results
        </span>
        <span v-else>
          Invitation Successful
        </span>
      </h2>
    </template>

    <template v-slot:body>
      <form-group class="add-actor-to-article__modal-content" v-if="showSuccessState">
        <div v-if="invitationMode === 'single'">
          {{ invitation.user.label }} was successfully invited to {{ community.name }}
        </div>
        <div v-else>
          <p v-if="bulkInvitationResults.successful.length > 0">
            <strong>{{ bulkInvitationResults.successful.length }}</strong> invitation(s) sent successfully
          </p>
          <p v-if="bulkInvitationResults.failed.length > 0" class="error">
            <strong>{{ bulkInvitationResults.failed.length }}</strong> invitation(s) failed
          </p>
          <div v-if="bulkInvitationResults.failed.length > 0 && allFailedEmailsHaveSameReason" class="error">
            <p><strong>Error:</strong> {{ bulkInvitationResults.failed[0].reason }}</p>
            <details>
              <summary>Show all {{ bulkInvitationResults.failed.length }} failed email addresses</summary>
              <ul>
                <li v-for="failure in bulkInvitationResults.failed" :key="failure.email">
                  {{ failure.email }}
                </li>
              </ul>
            </details>
          </div>
          <ul v-else-if="bulkInvitationResults.failed.length > 0" class="error">
            <li v-for="failure in bulkInvitationResults.failed" :key="failure.email">
              {{ failure.email }}: {{ failure.reason }}
            </li>
          </ul>
        </div>
      </form-group>

      <div v-if="!showSuccessState" class="invitation-mode-tabs">
        <button
          class="tab"
          :class="{ active: invitationMode === 'single' }"
          @click="invitationMode = 'single'"
          type="button"
        >
          Single member
        </button>
        <button
          class="tab"
          :class="{ active: invitationMode === 'bulk' }"
          @click="invitationMode = 'bulk'"
          type="button"
        >
          Multiple members
        </button>
      </div>

      <form-group v-if="!showSuccessState && invitationMode === 'single'" label="Search a user by name or email address" required
                  class="add-actor-to-article__modal-content">
        <autocomplete-dropdown
          is-simplified
          placeholder="Enter an email address or name"
          v-model="invitation.user"
          class="add-actor-to-article__dropdown"
          :required="true"
          :custom-no-result-message="'No user found. Press Enter to invite by email'"
          :options="displayedUserSuggestions"
          :do-search="autocompleteSearchTerm"
          :multiple="false"
          :allowClear="true"
          :margin-top="'0px'"
          :noResultsHandlerEnabled="true"
          :noResultMessage="{ text: 'Invite by email', smallText: 'Invite new user' }"
          @update:modelValue="verifyMember"
          @addNewItem="handleEmailInvitation"
          @search-change="handleSearchChange"
        />
        <p class="error">{{ error }}</p>
        <!--<p v-if="invitation.isEmailInvitation" class="info">
          You are inviting: <strong>{{ invitation.user.label }}</strong>
        </p>
        <p v-else-if="!invitation.user && currentSearchTerm && isValidEmail(currentSearchTerm)" class="info">
          Press "Invite community member" to send invitation to: <strong>{{ currentSearchTerm }}</strong>
        </p>-->
      </form-group>

      <form-group v-if="!showSuccessState && invitationMode === 'bulk'" label="Email addresses (comma or line separated) Max 100." required
                  class="add-actor-to-article__modal-content">
        <ds-textarea
          is-simplified
          v-model="bulkEmails"
          default-text="Enter email addresses separated by commas or new lines&#10;e.g. user1@example.com, user2@example.com&#10;or one per line"
          rows="6"
        />
        <p v-if="parsedBulkEmails.length > 0" class="info">
          {{ parsedBulkEmails.length }} email address(es) detected
        </p>
        <p class="error">{{ error }}</p>
      </form-group>

      <form-group v-if="!showSuccessState" label="Personal message" class="add-actor-to-article__modal-content">
        <ds-textarea is-simplified v-model="invitation.personal_message" placeholder="Personal message"/>
      </form-group>
    </template>

    <template v-slot:footer>
      <div v-if="!showSuccessState" class="add-member-to-community__modal-footer">
        <ds-button
          class="add-actor-to-article__button"
          :label="invitationMode === 'bulk' ? 'Invite community members' : 'Invite community member'"
          size="extra-small"
          variant="rounded"
          :disabled="isSubmitDisabled"
          @click="submit"
        />
      </div>
    </template>
  </modal>
</template>


<script>
  import MODAL_IDS from '../../constants/modal-ids.js'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '../../store/modules/ui.js'
  import Modal from './Modal.vue'
  import TranslationsMixin from '../../util/TranslationsMixin.js'
  import AutocompleteDropdown from '../Dropdown/AutocompleteDropdown.vue'
  import FormGroup from '../Form/FormGroup.vue'
  import DsInput from '../Form/DsInput.vue'
  import { searchUser } from '../../api/config'
  import { addCommunityMember, verifyMember } from '../../api/communities'
  import DsTextarea from '../Form/DsTextarea.vue'

  export default {
    data () {
      return {
        modalId: MODAL_IDS.ADD_MEMBER_TO_COMMUNITY_MODAL,
        message: '',
        actor: [],
        isLoading: false,
        isDisabled: true,
        currentSearchTerm: '',
        invitation: {
          user: null,
          personal_message: '',
          isEmailInvitation: false
        },
        error: '',
        displayedUserSuggestions: [],
        showSuccessState: false,
        invitationMode: 'single', // 'single' or 'bulk'
        bulkEmails: '',
        bulkInvitationResults: {
          successful: [],
          failed: []
        }
      }
    },
    computed: {
      ecosystemDisplayName () {
        return this.$store.getters.ecosystemDisplayName
      },
      communityId () {
        return this.community.id
      },
      community () {
        return this.modalContext.community
      },
      modalContext () {
        return this.$store.state.ui.modalContext
      },
      config () {
        return this.$store.state.config
      },
      parsedBulkEmails () {
        if (!this.bulkEmails || this.invitationMode !== 'bulk') {
          return []
        }

        // Split by comma, newline, or semicolon, then filter out empty strings
        const emails = this.bulkEmails
          .split(/[,;\n]+/)
          .map(email => email.trim())
          .filter(email => email.length > 0)

        return emails
      },
      allFailedEmailsHaveSameReason () {
        if (this.bulkInvitationResults.failed.length === 0) {
          return false
        }

        const firstReason = this.bulkInvitationResults.failed[0].reason
        return this.bulkInvitationResults.failed.every(failure => failure.reason === firstReason)
      },
      isSubmitDisabled () {
        if (this.isLoading) {
          return true
        }

        // Bulk mode validation (only for developers)
        if (this.invitationMode === 'bulk') {
          const validEmails = this.parsedBulkEmails.filter(email => this.isValidEmail(email))
          return validEmails.length === 0
        }

        // Single mode validation
        // If we have a user selected
        if (this.invitation.user) {
          // For email invitations, ensure it's a valid email
          if (this.invitation.isEmailInvitation) {
            return !this.isValidEmail(this.invitation.user.email)
          }
          // For existing users, ensure they have a userId and passed verification
          return !this.invitation.user.value || this.isDisabled
        }

        // If no user selected, check if current search term is a valid email
        if (this.currentSearchTerm && this.isValidEmail(this.currentSearchTerm)) {
          return false
        }

        return true
      }
    },
    methods: {
      isValidEmail (email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
      },
      handleEmailInvitation (emailAddress) {
        // Reset previous states
        this.error = ''
        this.invitation.isEmailInvitation = false

        if (!this.isValidEmail(emailAddress)) {
          this.error = 'Please enter a valid email address'
          this.isDisabled = true
          return
        }

        // Create a user object for email invitation
        this.invitation.user = {
          label: emailAddress,
          value: null, // No user ID for email invitations
          email: emailAddress
        }
        this.invitation.isEmailInvitation = true
        this.isDisabled = false
      },
      handleSearchChange (searchTerm) {
        this.currentSearchTerm = searchTerm

        // Reset email invitation state when search changes
        if (this.invitation.isEmailInvitation) {
          this.invitation.isEmailInvitation = false
          this.isDisabled = true
        }

        // If the search term is a valid email and no results are expected,
        // prepare for email invitation
        if (searchTerm && this.isValidEmail(searchTerm)) {
          // Don't automatically set it up, but show a hint
          this.error = ''
        }
      },
      verifyMember () {
        this.error = null
        this.isDisabled = true

        if (!this.invitation.user) {
          return
        }

        // Skip verification for email invitations
        if (this.invitation.isEmailInvitation) {
          this.isDisabled = false
          return
        }

        // Only verify if it's an existing user (has value/userId)
        if (!this.invitation.user.value) {
          return
        }

        verifyMember({ communityId: this.communityId, userId: this.invitation.user.value })
          .then(response => {
            this.isDisabled = false
            this.error = response.message
          })
          .catch(response => {
            this.isDisabled = true
            this.error = response.error
          })
      },
      close () {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, null)
        this.$store.commit(UI_MUTATION_TYPES.HIDE_MODAL, this.modalId)

        // Reset form state
        this.invitation.user = null
        this.invitation.personal_message = ''
        this.invitation.isEmailInvitation = false
        this.error = ''
        this.isDisabled = true
        this.invitationMode = 'single'
        this.bulkEmails = ''
        this.bulkInvitationResults = {
          successful: [],
          failed: []
        }

        this.$emit('close')
        this.showSuccessState = false
      },
      autocompleteSearchTerm (query) {
        return searchUser({
          query: query
        })
          .then(suggestions => {
            const options = []
            //suggestions = this.removeDuplicates(suggestions)

            suggestions
              .forEach(function (user) {
                options.push({
                  label: user.name + ' (' + user.email + ')',
                  value: user.id
                })
              })

            return options
          })
          .catch(err => {
            console.log(err)
          })
      },
      removeDuplicates (suggestions) {
        return suggestions
      },
      async submit () {
        this.error = null

        // Only allow bulk invitations for developers
        if (this.invitationMode === 'bulk') {
          await this.submitBulkInvitations()
        } else {
          await this.submitSingleInvitation()
        }
      },
      async submitSingleInvitation () {
        // Check if user typed an email but didn't formally select it
        if (!this.invitation.user && this.currentSearchTerm && this.isValidEmail(this.currentSearchTerm)) {
          // Set up email invitation from search term
          this.handleEmailInvitation(this.currentSearchTerm)
        }

        // Ensure we have a valid invitation
        if (!this.invitation.user) {
          this.error = 'Please select a user or enter a valid email address'
          return
        }

        let data = {
          personal_message: this.invitation.personal_message
        }

        // Handle email invitations vs existing user invitations
        if (this.invitation.isEmailInvitation) {
          data.email = this.invitation.user.email
        } else {
          data.userId = this.invitation.user.value
        }

        this.isLoading = true

        addCommunityMember({ communityId: this.communityId, data: data })
          .then(response => {
            if (response.error) {
              this.error = response.error
              return
            }

            if (response.success) {
              this.showSuccessState = true
              this.$bus.emit('invited-community-member')
            }

            this.userId = null
            this.invitation.personal_message = null
          })
          .catch(error => {
            this.error = error.response?.data?.message || 'An error occurred while sending the invitation'
          })
          .finally(() => {
            this.isLoading = false
          })
      },
      async submitBulkInvitations () {
        // Reset results
        this.bulkInvitationResults = {
          successful: [],
          failed: []
        }

        // Filter to only valid emails
        const validEmails = this.parsedBulkEmails.filter(email => this.isValidEmail(email))
        const invalidEmails = this.parsedBulkEmails.filter(email => !this.isValidEmail(email))

        // Add invalid emails to failed list
        invalidEmails.forEach(email => {
          this.bulkInvitationResults.failed.push({
            email: email,
            reason: 'Invalid email format'
          })
        })

        if (validEmails.length === 0) {
          this.error = 'No valid email addresses found'
          return
        }

        this.isLoading = true

        try {
          // Send all emails in a single bulk request
          const data = {
            emails: validEmails,
            personal_message: this.invitation.personal_message
          }

          const response = await addCommunityMember({
            communityId: this.communityId,
            data: data
          })

          if (response.results) {
            // Merge backend results with frontend validation results
            this.bulkInvitationResults.successful = response.results.successful || []
            this.bulkInvitationResults.failed = [
              ...this.bulkInvitationResults.failed,
              ...(response.results.failed || [])
            ]
          } else if (response.error) {
            // If there's a general error, mark all as failed
            validEmails.forEach(email => {
              this.bulkInvitationResults.failed.push({
                email: email,
                reason: response.error
              })
            })
          }
        } catch (error) {
          console.error('Bulk invitation error:', error)

          // Extract the actual error message from various possible locations
          let errorMessage = 'An error occurred while sending the invitation'

          // Helper function to extract error from data object
          const extractErrorFromData = (data) => {
            if (!data) return null

            // Handle Laravel validation errors (array format)
            if (Array.isArray(data)) {
              return data[0]
            }

            if (typeof data === 'object') {
              // Check if this is a Laravel validation error object (field names with array values)
              // e.g., { emails: ["error message"], statusCode: 422 }
              const keys = Object.keys(data).filter(key => key !== 'statusCode')
              if (keys.length > 0 && Array.isArray(data[keys[0]])) {
                return data[keys[0]][0]
              }

              // Handle nested errors format
              if (data.errors) {
                const firstError = Object.values(data.errors)[0]
                return Array.isArray(firstError) ? firstError[0] : firstError
              }

              // Handle direct error/message properties
              if (data.error) return data.error
              if (data.message) return data.message
            }

            if (typeof data === 'string') {
              return data
            }

            return null
          }

          // Try to extract error from the error object itself first
          const extractedError = extractErrorFromData(error)
          if (extractedError) {
            errorMessage = extractedError
          } else if (error.response?.data) {
            // Try error.response.data (standard axios format)
            const extractedResponseError = extractErrorFromData(error.response.data)
            if (extractedResponseError) {
              errorMessage = extractedResponseError
            }
          } else if (error.message) {
            errorMessage = error.message
          }

          // If the request fails, mark all valid emails as failed with the same error
          validEmails.forEach(email => {
            this.bulkInvitationResults.failed.push({
              email: email,
              reason: errorMessage
            })
          })
        }

        this.isLoading = false

        // Always show results state for bulk invitations to display detailed errors
        this.showSuccessState = true

        // Emit event only if at least one invitation succeeded
        if (this.bulkInvitationResults.successful.length > 0) {
          this.$bus.emit('invited-community-member')
        }
      }
    },
    mounted () {

    },
    components: {
      Modal,
      AutocompleteDropdown,
      FormGroup,
      DsInput,
      DsTextarea
    },
    mixins: [TranslationsMixin]
  }
</script>


<style lang="scss" scoped>
  @import '../../../scss/_variables.scss';

  .add-actor-to-article__modal-content {
    box-shadow: none;
    border-radius: 0;
    margin: 10px;
    padding: 10px;
  }

  .add-actor-to-article__modal-label {
    font-size: 12px;
  }

  .error {
    color: $color-error;
  }

  .info {
    color: $color-primary;
    font-size: 14px;
    margin-top: 8px;
  }

  .with-subtitle.modal__simplified :deep(.modal__wrapper .modal__container .modal__body) {
    overflow: visible;
  }

  :deep(.multiselect__content-wrapper) {
    max-height: 84px !important;
  }

  .invitation-mode-tabs {
    display: flex;
    border-bottom: 2px solid #e5e7eb;
    margin: 0 10px 20px;
  }

  .invitation-mode-tabs .tab {
    flex: 1;
    padding: 12px 24px;
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    color: #6b7280;
    transition: all 0.2s ease;
    outline: none;
  }

  .invitation-mode-tabs .tab:hover {
    color: #374151;
    background-color: #f9fafb;
  }

  .invitation-mode-tabs .tab.active {
    color: $color-primary;
    border-bottom-color: $color-primary;
  }

  details {
    margin-top: 10px;
    cursor: pointer;
  }

  details summary {
    font-weight: 500;
    padding: 8px 0;
    color: $color-error;
  }

  details summary:hover {
    text-decoration: underline;
  }

  details ul {
    margin-top: 10px;
    margin-left: 20px;
    max-height: 300px;
    overflow-y: auto;
  }

</style>
